/* =========================================================================
   Mr Beard — theme.js
   Zenith Weave

   No framework, no build step. Behaviour ships as custom elements so a
   section can be added, removed or reordered in the theme editor and its
   JavaScript wires itself up automatically.

   Everything degrades: with JS disabled the store still renders, navigates
   and checks out through standard form posts.
   ========================================================================= */

(function () {
  'use strict';

  document.documentElement.classList.remove('no-js');

  /* -----------------------------------------------------------------------
     Config injected by layout/theme.liquid
     -------------------------------------------------------------------- */

  const config = window.MrBeard || {};
  const routes = config.routes || {};
  const strings = config.strings || {};

  /* -----------------------------------------------------------------------
     Helpers
     -------------------------------------------------------------------- */

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const debounce = (fn, wait = 250) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  /* Follows the shop's money format the way Liquid's
     money_without_trailing_zeros does: whole amounts drop the ".00". */
  const formatMoney = (cents) => {
    const value = Number(cents) || 0;
    const whole = value % 100 === 0;
    const number = (sep, dec, places) => {
      const fixed = (value / 100).toFixed(places);
      const [int, frac] = fixed.split('.');
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
      return frac && !whole ? `${grouped}${dec}${frac}` : grouped;
    };

    const format = config.moneyFormat || '';
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (match) {
      let amount;
      switch (match[1]) {
        case 'amount_no_decimals': amount = number(',', '.', 0); break;
        case 'amount_with_comma_separator': amount = number('.', ',', 2); break;
        case 'amount_no_decimals_with_comma_separator': amount = number('.', ',', 0); break;
        case 'amount_with_apostrophe_separator': amount = number("'", '.', 2); break;
        default: amount = number(',', '.', 2);
      }
      return format.replace(match[0], amount);
    }

    if (config.currency) {
      try {
        return new Intl.NumberFormat(config.locale || 'en', {
          style: 'currency',
          currency: config.currency,
          minimumFractionDigits: whole ? 0 : 2
        }).format(value / 100);
      } catch (error) {
        /* Fall through to the plain number below. */
      }
    }
    return number(',', '.', 2);
  };

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary';

  function trapFocus(container, opener) {
    const focusables = () =>
      Array.from(container.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null
      );

    function onKeydown(event) {
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeydown);
    const target = container.querySelector('[autofocus]') || focusables()[0];
    if (target) target.focus();

    return function release() {
      container.removeEventListener('keydown', onKeydown);
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }

  let scrollLockCount = 0;

  function lockScroll() {
    scrollLockCount += 1;
    document.body.classList.add('overflow-hidden');
  }

  function unlockScroll() {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount === 0) document.body.classList.remove('overflow-hidden');
  }

  async function fetchSection(url) {
    const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
    const text = await response.text();
    return new DOMParser().parseFromString(text, 'text/html');
  }

  /* -----------------------------------------------------------------------
     Header — measure height, flag scrolled state
     -------------------------------------------------------------------- */

  function initHeader() {
    const wrapper = document.querySelector('[data-header-wrapper]');
    if (!wrapper) return;

    // Sticky has to live on the section wrapper. An element only sticks
    // within its own parent, and this wrapper's parent is exactly as tall as
    // the header, so it would have nowhere to travel.
    if (wrapper.dataset.sticky === 'true') {
      const shell = wrapper.closest('.shopify-section');
      if (shell) shell.classList.add('header-shell--sticky');
    }

    const setHeight = () => {
      document.documentElement.style.setProperty(
        '--header-height',
        `${wrapper.offsetHeight}px`
      );
    };

    setHeight();
    window.addEventListener('resize', debounce(setHeight, 150));

    const header = wrapper.querySelector('[data-header]');
    if (!header) return;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        header.classList.toggle('header--scrolled', window.scrollY > 8);
        ticking = false;
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* -----------------------------------------------------------------------
     Page fade

     Browsers with cross-document view transitions handle this in CSS alone.
     Everywhere else, fade the page out as a same-site link is followed. The
     guards matter: a modified click, a new tab, a download or an in-page
     anchor must not be intercepted.
     -------------------------------------------------------------------- */

  function initPageTransition() {
    const root = document.documentElement;
    if (root.dataset.pageTransition !== 'fade') return;

    const supportsViewTransitions =
      'startViewTransition' in document && CSS.supports('selector(::view-transition)');

    if (supportsViewTransitions) {
      root.classList.add('vt-supported');
      return;
    }

    if (prefersReducedMotion()) return;

    document.addEventListener('click', (event) => {
      if (event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const link = event.target.closest('a[href]');
      if (!link) return;
      if (link.target && link.target !== '_self') return;
      if (link.hasAttribute('download')) return;
      if (link.dataset.noTransition !== undefined) return;

      const href = link.getAttribute('href');
      if (!href || href.startsWith('#')) return;

      let url;
      try {
        url = new URL(link.href);
      } catch (error) {
        return;
      }

      if (url.origin !== window.location.origin) return;
      if (!/^https?:$/.test(url.protocol)) return;
      // Same page, different hash — no navigation to cover.
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;

      event.preventDefault();
      root.classList.add('is-leaving');

      const duration =
        (parseInt(getComputedStyle(root).getPropertyValue('--page-fade'), 10) || 800) * 0.7;

      window.setTimeout(() => {
        window.location.href = link.href;
      }, duration);
    });

    // Coming back via the browser's cache would otherwise show a faded page.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) root.classList.remove('is-leaving');
    });
  }

  /* -----------------------------------------------------------------------
     <back-to-top> — appears once the page has scrolled
     -------------------------------------------------------------------- */

  class BackToTop extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;

      this.threshold = parseInt(this.dataset.threshold, 10) || 600;

      this.button.addEventListener('click', () => {
        window.scrollTo({
          top: 0,
          behavior: prefersReducedMotion() ? 'auto' : 'smooth'
        });
      });

      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          this.classList.toggle('is-visible', window.scrollY > this.threshold);
          ticking = false;
        });
      };

      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  }

  customElements.define('back-to-top', BackToTop);

  /* -----------------------------------------------------------------------
     System showcase rails

     When the products sit on the left, the card that should be clipped is the
     one at the page edge. A scroll container always starts at zero, which
     would clip the wrong end, so the rail is nudged to its far end once.
     Only above the two column breakpoint — on mobile it reads left to right.
     -------------------------------------------------------------------- */

  function initSystemRails() {
    if (!window.matchMedia('(min-width: 64em)').matches) return;

    document.querySelectorAll('.system--end .system__rail-track').forEach((track) => {
      if (track.dataset.nudged === 'true') return;
      if (track.scrollWidth <= track.clientWidth) return;

      // The rail scrolls smoothly by design, which would make this initial
      // jump animate in front of the visitor. Apply it instantly instead.
      const previous = track.style.scrollBehavior;
      track.style.scrollBehavior = 'auto';

      const rtl = document.documentElement.dir === 'rtl';
      track.scrollLeft = rtl ? -track.scrollWidth : track.scrollWidth;

      track.style.scrollBehavior = previous;
      track.dataset.nudged = 'true';
    });
  }

  /* -----------------------------------------------------------------------
     Scroll reveal
     -------------------------------------------------------------------- */

  function initReveal() {
    const items = document.querySelectorAll('.reveal:not(.is-revealed)');
    if (!items.length) return;

    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    items.forEach((el) => observer.observe(el));
  }

  /* -----------------------------------------------------------------------
     <drawer-element> — mobile menu, cart, filters
     -------------------------------------------------------------------- */

  class DrawerElement extends HTMLElement {
    connectedCallback() {
      this.releaseFocus = null;
      this.opener = null;

      this.addEventListener('click', (event) => {
        if (
          event.target.closest('[data-drawer-close]') ||
          event.target.hasAttribute('data-drawer-overlay')
        ) {
          this.close();
        }
      });

      this.onKeydown = (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
      };
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
    }

    open(opener) {
      if (this.classList.contains('is-open')) return;
      this.opener = opener || null;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll();
      this.releaseFocus = trapFocus(this, this.opener);
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      unlockScroll();
      if (this.releaseFocus) this.releaseFocus();
      this.releaseFocus = null;
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }

    toggle(opener) {
      this.classList.contains('is-open') ? this.close() : this.open(opener);
    }
  }

  customElements.define('drawer-element', DrawerElement);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const drawer = document.getElementById(trigger.getAttribute('data-drawer-open'));
    if (!drawer) return;
    event.preventDefault();
    drawer.open(trigger);

    // A trigger can ask for a specific pane, so one drawer can be entered at
    // the cart or at the favourites tab.
    // A plain trigger (header cart icon) always lands on the cart.
    const tabs = drawer.querySelector('cart-tabs');
    const pane = trigger.dataset.drawerPane || (tabs ? 'cart' : '');
    if (pane && tabs && typeof tabs.show === 'function') tabs.show(pane);
  });

  /* -----------------------------------------------------------------------
     <quantity-input>
     -------------------------------------------------------------------- */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;

      this.querySelectorAll('button').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const step = button.dataset.action === 'increase' ? 1 : -1;
          const min = parseInt(this.input.min || '0', 10);
          const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
          const next = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || 0) + step));
          if (next === parseInt(this.input.value, 10)) return;
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }

  customElements.define('quantity-input', QuantityInput);

  /* -----------------------------------------------------------------------
     Cart
     -------------------------------------------------------------------- */

  const Cart = {
    async request(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      if (!response.ok) throw data;
      return data;
    },

    sectionsToRender() {
      return ['cart-drawer', 'cart-bubble'];
    },

    async add(formData) {
      const body = {
        items: [
          {
            id: formData.get('id'),
            quantity: parseInt(formData.get('quantity') || '1', 10)
          }
        ],
        sections: this.sectionsToRender().join(','),
        sections_url: window.location.pathname
      };

      const properties = {};
      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match) properties[match[1]] = value;
      });
      if (Object.keys(properties).length) body.items[0].properties = properties;

      const selling_plan = formData.get('selling_plan');
      if (selling_plan) body.items[0].selling_plan = selling_plan;

      return this.request(`${routes.cart_add_url || '/cart/add'}.js`, body);
    },

    async change(line, quantity) {
      return this.request(`${routes.cart_change_url || '/cart/change'}.js`, {
        line,
        quantity,
        sections: this.sectionsToRender().join(','),
        sections_url: window.location.pathname
      });
    },

    applySections(sections) {
      if (!sections) return;

      Object.keys(sections).forEach((id) => {
        const markup = sections[id];
        if (typeof markup !== 'string') return;

        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const source = parsed.querySelector(`[data-section-content="${id}"]`);
        const target = document.querySelector(`[data-section-content="${id}"]`);
        if (source && target) target.innerHTML = source.innerHTML;
      });

      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      initReveal();
    }
  };

  window.MrBeardCart = Cart;

  /* -----------------------------------------------------------------------
     <product-form> — AJAX add to cart
     -------------------------------------------------------------------- */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.button = this.querySelector('[data-add-to-cart]');
      this.errorTarget = this.querySelector('[data-cart-error]');
      this.openDrawer = this.dataset.openDrawer !== 'false';

      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    async onSubmit(event) {
      // Without JS this posts normally; with JS we intercept.
      event.preventDefault();
      if (!this.button || this.button.hasAttribute('aria-disabled')) return;

      this.setLoading(true);
      this.clearError();

      try {
        const data = await Cart.add(new FormData(this.form));
        Cart.applySections(data.sections);

        const drawer = document.getElementById('cart-drawer');
        if (this.openDrawer && drawer && config.cartType === 'drawer') {
          drawer.open(this.button);
        } else if (config.cartType !== 'drawer') {
          window.location.href = routes.cart_url || '/cart';
          return;
        }
      } catch (error) {
        this.showError(
          (error && (error.description || error.message)) ||
            strings.cartError ||
            'Something went wrong. Please try again.'
        );
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(loading) {
      if (!this.button) return;
      this.button.classList.toggle('is-loading', loading);
      this.button.toggleAttribute('aria-busy', loading);
      this.button.toggleAttribute('aria-disabled', loading);
      this.button.toggleAttribute('disabled', loading);
      // Only swap text on buttons that actually carry a word. Icon-only
      // buttons show a spinner via the is-loading class instead.
      const label = this.button.querySelector('[data-button-label]');
      if (label && label.children.length > 0) return;

      if (label && loading) {
        label.dataset.original = label.dataset.original || label.textContent;
        label.textContent = strings.adding || 'Adding…';
      } else if (label && label.dataset.original) {
        label.textContent = label.dataset.original;
      }
    }

    showError(message) {
      if (!this.errorTarget) return;
      this.errorTarget.textContent = message;
      this.errorTarget.hidden = false;
    }

    clearError() {
      if (!this.errorTarget) return;
      this.errorTarget.textContent = '';
      this.errorTarget.hidden = true;
    }
  }

  customElements.define('product-form', ProductForm);

  /* -----------------------------------------------------------------------
     <cart-items> — quantity change / remove inside drawer and cart page
     -------------------------------------------------------------------- */

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-quantity-input]');
        if (!input) return;
        this.updateLine(input.dataset.line, parseInt(input.value, 10));
      });

      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-remove-line]');
        if (remove) {
          event.preventDefault();
          this.updateLine(remove.dataset.removeLine, 0);
          return;
        }

        const step = event.target.closest('[data-quantity-step]');
        if (!step) return;
        event.preventDefault();

        const input = this.querySelector(`[data-quantity-input][data-line="${step.dataset.line}"]`);
        const current = input ? parseInt(input.value, 10) || 0 : 0;
        const next = Math.max(0, current + parseInt(step.dataset.quantityStep, 10));
        this.updateLine(step.dataset.line, next);
      });
    }

    async updateLine(line, quantity) {
      if (!line || Number.isNaN(quantity)) return;
      this.classList.add('is-loading');

      try {
        const data = await Cart.change(parseInt(line, 10), quantity);
        Cart.applySections(data.sections);
      } catch (error) {
        window.location.href = routes.cart_url || '/cart';
      } finally {
        this.classList.remove('is-loading');
      }
    }
  }

  customElements.define('cart-items', CartItems);

  /* -----------------------------------------------------------------------
     <cart-recommendations> — "You may also like" row in the v2 cart drawer

     Loads sections/cart-drawer-recs.liquid (Shopify product recommendations
     for the first cart line, or the fallback collection) the first time the
     drawer opens. The drawer is re-rendered after every cart change, which
     reconnects this element, so markup is cached per URL to avoid a refetch
     and a jump at the bottom of the drawer. Products already in the cart are
     dropped client-side, so a cached row never offers what was just added.
     -------------------------------------------------------------------- */

  const recsCache = new Map();

  class CartRecommendations extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-recs-track]');
      this.url = this.dataset.url;
      if (!this.track || !this.url) return;

      this.addEventListener('change', (event) => {
        const select = event.target.closest('[data-rec-variant]');
        if (!select) return;
        const option = select.selectedOptions[0];
        const card = select.closest('.mc2-rec');
        const price = card && card.querySelector('[data-rec-price]');
        if (price && option && option.dataset.price) price.textContent = option.dataset.price;
      });

      if (recsCache.has(this.url)) {
        this.fill(recsCache.get(this.url));
        return;
      }

      const drawer = this.closest('drawer-element');
      if (!drawer || drawer.classList.contains('is-open')) {
        this.load();
      } else {
        drawer.addEventListener('drawer:open', () => this.load(), { once: true });
      }
    }

    async load() {
      if (recsCache.has(this.url)) {
        this.fill(recsCache.get(this.url));
        return;
      }
      try {
        const response = await fetch(this.url);
        if (!response.ok) throw new Error(response.status);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = doc.querySelector('[data-cart-recs-content]');
        const markup = content ? content.innerHTML.trim() : '';
        recsCache.set(this.url, markup);
        if (this.isConnected) this.fill(markup);
      } catch (error) {
        this.hidden = true;
      }
    }

    fill(markup) {
      this.track.innerHTML = markup;
      const inCart = (this.dataset.cartIds || '').split(',').filter(Boolean);
      const limit = parseInt(this.dataset.limit, 10) || 6;
      let shown = 0;
      this.track.querySelectorAll('[data-product-id]').forEach((card) => {
        if (inCart.includes(card.dataset.productId) || shown >= limit) {
          card.remove();
        } else {
          shown += 1;
        }
      });
      this.hidden = shown === 0;
      const slider = this.querySelector('slider-element');
      if (slider && typeof slider.updateButtons === 'function') {
        requestAnimationFrame(() => slider.updateButtons());
      }
    }
  }

  customElements.define('cart-recommendations', CartRecommendations);

  /* -----------------------------------------------------------------------
     <discount-field> — applies a code from the mini cart

     Shopify has no cart API for discount codes, so this uses the /discount
     route, which records the code and returns to the page given in redirect.
     -------------------------------------------------------------------- */

  class DiscountField extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.button = this.querySelector('[data-discount-apply]');
      if (!this.input || !this.button) return;

      const apply = () => {
        const code = this.input.value.trim();
        if (!code) {
          this.input.focus();
          return;
        }
        this.button.classList.add('is-loading');
        const back = window.location.pathname + window.location.search;
        window.location.href =
          '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(back);
      };

      this.button.addEventListener('click', apply);
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          apply();
        }
      });
    }
  }

  customElements.define('discount-field', DiscountField);

  /* -----------------------------------------------------------------------
     <variant-picker> — re-renders price, availability and buy button
     -------------------------------------------------------------------- */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.productUrl = this.dataset.url;
      this.variants = this.parseVariants();

      this.addEventListener('change', this.onChange.bind(this));
      this.updateAvailability();
    }

    parseVariants() {
      const script = this.querySelector('[data-variant-data]');
      if (!script) return [];
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return [];
      }
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('input[type="radio"]:checked, select')).map(
        (input) => input.value
      );
    }

    onChange() {
      const selected = this.selectedOptions;
      const variant = this.variants.find((candidate) =>
        candidate.options.every((option, index) => option === selected[index])
      );

      this.updateOptionLabels();
      this.updateAvailability();

      if (!variant) {
        this.setUnavailable();
        return;
      }

      this.updateUrl(variant);
      this.updateFormId(variant);
      this.renderSection(variant);
    }

    updateOptionLabels() {
      this.querySelectorAll('[data-option-value]').forEach((label) => {
        const input = this.querySelector(`#${label.dataset.optionValue}`);
        if (input && input.checked) label.textContent = input.value;
      });
      // The "Select scent: Lumberjack" value beside each option name.
      this.querySelectorAll('fieldset.variant-option').forEach((fieldset) => {
        const value = fieldset.querySelector('.variant-option__value');
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (value && checked) value.textContent = checked.value;
      });
    }

    /* Liquid marks sold-out values on the first render, but on a product with
       more than one option whether a value is buyable depends on what else is
       selected — so the same test re-runs after every change. */
    updateAvailability() {
      const style = this.dataset.soldOutStyle;
      if (style === 'normal' || !this.variants.length) return;

      const groups = Array.from(this.querySelectorAll('.variant-option'));
      const chosen = groups.map((group) => {
        const checked = group.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      });

      groups.forEach((group, index) => {
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          const probe = chosen.slice();
          probe[index] = input.value;

          /* Buyable when some in-stock variant matches every option the
             shopper has actually settled on. Positions still undecided are
             left open rather than treated as a mismatch. */
          const buyable = this.variants.some(
            (variant) =>
              variant.available &&
              probe.every(
                (value, position) => value === null || variant.options[position] === value
              )
          );

          if (style !== 'fade') input.disabled = !buyable;
          input.classList.toggle('is-unavailable', !buyable);

          const label = input.nextElementSibling;
          const tag = label && label.querySelector('.scent-tile__oos');
          if (tag) tag.hidden = buyable;
        });
      });
    }

    setUnavailable() {
      const button = document.querySelector(`[data-add-to-cart][form="product-form-${this.sectionId}"], #product-form-${this.sectionId} [data-add-to-cart]`);
      if (!button) return;
      button.setAttribute('aria-disabled', 'true');
      const label = button.querySelector('[data-button-label]');
      if (label) label.textContent = strings.unavailable || 'Unavailable';
    }

    updateUrl(variant) {
      if (!this.productUrl || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.productUrl}?variant=${variant.id}`);
    }

    updateFormId(variant) {
      const input = document.querySelector(`#product-form-${this.sectionId} [name="id"]`);
      if (input) {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    async renderSection(variant) {
      this.classList.add('is-loading');

      try {
        const doc = await fetchSection(
          `${this.productUrl}?variant=${variant.id}&section_id=${this.sectionId}`
        );

        ['price', 'inventory', 'buy-buttons', 'sku', 'gallery-active'].forEach((target) => {
          const source = doc.querySelector(`[data-variant-target="${target}"]`);
          const destination = document.querySelector(`[data-variant-target="${target}"]`);
          if (source && destination) destination.innerHTML = source.innerHTML;
        });

        this.dispatchEvent(
          new CustomEvent('variant:change', { bubbles: true, detail: { variant } })
        );
      } catch (error) {
        /* Leave the previous state in place — the form still posts correctly. */
      } finally {
        this.classList.remove('is-loading');
      }
    }
  }

  customElements.define('variant-picker', VariantPicker);

  /* -----------------------------------------------------------------------
     <media-gallery>
     -------------------------------------------------------------------- */

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.viewer = this.querySelector('[data-gallery-viewer]');
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      if (!this.viewer) return;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', (event) => {
          event.preventDefault();
          this.show(thumb.dataset.mediaId);
        });
      });

      this.addEventListener('variant:change', (event) => {
        const media = event.detail && event.detail.variant && event.detail.variant.featured_media;
        if (media) this.show(String(media.id));
      });
    }

    show(mediaId) {
      const slide = this.viewer.querySelector(`[data-media-id="${mediaId}"]`);
      if (!slide) return;

      this.viewer.scrollTo({
        left: slide.offsetLeft - this.viewer.offsetLeft,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });

      this.thumbs.forEach((thumb) => {
        thumb.setAttribute('aria-current', String(thumb.dataset.mediaId === mediaId));
      });
    }
  }

  customElements.define('media-gallery', MediaGallery);

  /* -----------------------------------------------------------------------
     <slider-element>
     -------------------------------------------------------------------- */

  class SliderElement extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');
      if (!this.track) return;

      const step = () => {
        const first = this.track.querySelector('.slider__item');
        return first ? first.offsetWidth + parseFloat(getComputedStyle(this.track).columnGap || 0) : 320;
      };

      const scrollBy = (direction) => {
        const rtl = document.documentElement.dir === 'rtl';
        this.track.scrollBy({
          left: step() * direction * (rtl ? -1 : 1),
          behavior: prefersReducedMotion() ? 'auto' : 'smooth'
        });
      };

      if (this.prev) this.prev.addEventListener('click', () => scrollBy(-1));
      if (this.next) this.next.addEventListener('click', () => scrollBy(1));

      this.track.addEventListener('scroll', debounce(() => this.updateButtons(), 80), {
        passive: true
      });
      window.addEventListener('resize', debounce(() => this.updateButtons(), 150));
      this.updateButtons();

      this.dots = this.querySelector('[data-slider-dots]');
      if (this.dots) {
        this.buildDots();
        this.track.addEventListener('scroll', debounce(() => this.updateDots(), 60), { passive: true });
        window.addEventListener('resize', debounce(() => this.buildDots(), 150));
      }
    }

    /* One dot per viewport-width "page" of the track, rebuilt on resize since
       the page count changes with how many cards fit. */
    buildDots() {
      const { scrollWidth, clientWidth } = this.track;
      const pages = Math.max(1, Math.ceil((scrollWidth - 4) / clientWidth));
      this.dots.innerHTML = '';
      this.dots.hidden = pages < 2;
      for (let i = 0; i < pages; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'slider-dots__dot';
        dot.setAttribute('aria-label', `${i + 1} / ${pages}`);
        dot.addEventListener('click', () => {
          const rtl = document.documentElement.dir === 'rtl';
          this.track.scrollTo({
            left: i * clientWidth * (rtl ? -1 : 1),
            behavior: prefersReducedMotion() ? 'auto' : 'smooth'
          });
        });
        this.dots.appendChild(dot);
      }
      this.updateDots();
    }

    updateDots() {
      if (!this.dots) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const dots = this.dots.children;
      const atEnd = Math.abs(scrollLeft) + clientWidth >= scrollWidth - 4;
      const active = atEnd ? dots.length - 1 : Math.round(Math.abs(scrollLeft) / clientWidth);
      Array.from(dots).forEach((dot, index) => {
        dot.setAttribute('aria-current', index === active ? 'true' : 'false');
      });
    }

    updateButtons() {
      if (!this.prev || !this.next) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const position = Math.abs(scrollLeft);
      this.prev.disabled = position < 4;
      this.next.disabled = position + clientWidth >= scrollWidth - 4;
    }
  }

  customElements.define('slider-element', SliderElement);

  /* -----------------------------------------------------------------------
     Drag to scroll — with a mouse, sideways carousels only move with a
     trackpad or shift + wheel, so any slider track (and the loop rail) can
     also be grabbed and pulled. Touch keeps the browser's own swipe. A drag
     suppresses the click at the end so a card is not opened by accident.
     -------------------------------------------------------------------- */

  (function enableDragScroll() {
    const selector = '[data-slider-track], [data-loop-track]';
    let track = null;
    let startX = 0;
    let startLeft = 0;
    let moved = false;

    document.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      const found = event.target.closest(selector);
      if (!found || found.scrollWidth <= found.clientWidth + 2) return;
      if (event.target.closest('input, select, textarea, button, [data-review-open]')) return;
      track = found;
      startX = event.clientX;
      startLeft = track.scrollLeft;
      moved = false;
    });

    document.addEventListener('pointermove', (event) => {
      if (!track) return;
      const dx = event.clientX - startX;
      if (!moved && Math.abs(dx) < 6) return;
      if (!moved) {
        moved = true;
        track.classList.add('is-dragging');
      }
      track.scrollLeft = startLeft - dx;
      event.preventDefault();
    });

    const end = () => {
      if (!track) return;
      const current = track;
      track = null;
      if (!moved) return;
      current.classList.remove('is-dragging');
      /* Let the browser snap to the nearest card once snapping is back on. */
      current.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
      }, { capture: true, once: true });
      setTimeout(() => current.dispatchEvent(new Event('scroll')), 0);
    };

    document.addEventListener('pointerup', end);
    document.addEventListener('pointercancel', end);
    document.addEventListener('dragstart', (event) => {
      if (event.target.closest(selector)) event.preventDefault();
    });
  })();

  /* --------------------------------------------------------------------
     <loop-rail> — a full-bleed card rail that never runs out

     The cards are cloned once before and once after (inert, hidden from
     assistive tech). When the scroll drifts into a clone set it jumps by
     exactly one set width, which lands on the same snap point, so the
     shopper never sees the seam. One dot per real card.
     -------------------------------------------------------------------- */

  class LoopRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-loop-track]');
      this.dots = this.querySelector('[data-loop-dots]');
      if (!this.track || this.dataset.ready) return;
      this.dataset.ready = 'true';

      this.items = Array.from(this.track.children);
      this.count = this.items.length;
      if (this.count < 2) return;

      const clone = () => {
        const fragment = document.createDocumentFragment();
        this.items.forEach((item) => {
          const copy = item.cloneNode(true);
          copy.setAttribute('aria-hidden', 'true');
          copy.setAttribute('inert', '');
          copy.removeAttribute('id');
          fragment.appendChild(copy);
        });
        return fragment;
      };
      this.track.prepend(clone());
      this.track.append(clone());

      this.buildDots();
      requestAnimationFrame(() => this.jump(this.base()));

      this.track.addEventListener('scroll', debounce(() => this.settle(), 120), { passive: true });
      window.addEventListener('resize', debounce(() => this.jump(this.base() + this.index() * this.pitch()), 150));

      if (this.hasAttribute('data-autoplay') && !prefersReducedMotion()) this.enableAutoplay();
    }

    // Slow autoplay: one card every few seconds, paused on hover, touch,
    // focus, off screen and in a background tab.
    enableAutoplay() {
      const delay = parseInt(this.dataset.autoplay, 10) || 4500;
      let paused = false;
      let visible = true;
      const tick = () => {
        if (paused || !visible || document.hidden) return;
        this.track.scrollTo({ left: this.track.scrollLeft + this.pitch(), behavior: 'smooth' });
      };
      this.timer = setInterval(tick, delay);
      let resume;
      const pause = () => { paused = true; clearTimeout(resume); };
      const play = (wait) => { clearTimeout(resume); resume = setTimeout(() => { paused = false; }, wait || 0); };
      this.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') pause(); });
      this.addEventListener('pointerleave', (event) => { if (event.pointerType === 'mouse') play(); });
      this.addEventListener('touchstart', pause, { passive: true });
      this.addEventListener('touchend', () => play(delay), { passive: true });
      this.addEventListener('focusin', pause);
      this.addEventListener('focusout', () => play());
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => { visible = entries[0].isIntersecting; }).observe(this);
      }
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    snapOffset() {
      return parseFloat(getComputedStyle(this.track).scrollPaddingInlineStart) || 0;
    }

    // Scroll position that puts the first real card on its snap line.
    base() {
      return this.items[0].offsetLeft - this.snapOffset();
    }

    setWidth() {
      return this.items[0].offsetLeft - this.track.children[0].offsetLeft;
    }

    pitch() {
      return this.count > 1 ? this.items[1].offsetLeft - this.items[0].offsetLeft : this.setWidth();
    }

    index() {
      const raw = Math.round((this.track.scrollLeft - this.base()) / this.pitch());
      return ((raw % this.count) + this.count) % this.count;
    }

    jump(left) {
      const behavior = this.track.style.scrollBehavior;
      this.track.style.scrollBehavior = 'auto';
      this.track.scrollLeft = left;
      this.track.style.scrollBehavior = behavior;
      this.updateDots();
    }

    settle() {
      const base = this.base();
      const width = this.setWidth();
      const left = this.track.scrollLeft;
      if (left < base - width / 2) this.jump(left + width);
      else if (left > base + width / 2) this.jump(left - width);
      else this.updateDots();
    }

    buildDots() {
      if (!this.dots) return;
      this.dots.innerHTML = '';
      this.dots.hidden = false;
      for (let i = 0; i < this.count; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'slider-dots__dot';
        dot.setAttribute('aria-label', `${i + 1} / ${this.count}`);
        dot.addEventListener('click', () => {
          this.track.scrollTo({
            left: this.base() + i * this.pitch(),
            behavior: prefersReducedMotion() ? 'auto' : 'smooth'
          });
        });
        this.dots.appendChild(dot);
      }
    }

    updateDots() {
      if (!this.dots) return;
      const active = this.index();
      Array.from(this.dots.children).forEach((dot, i) => {
        dot.setAttribute('aria-current', i === active ? 'true' : 'false');
      });
    }
  }

  customElements.define('loop-rail', LoopRail);

  /* -----------------------------------------------------------------------
     <announcement-rotator>
     -------------------------------------------------------------------- */

  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.messages = Array.from(this.querySelectorAll('[data-announcement]'));
      if (this.messages.length < 2 || prefersReducedMotion()) {
        if (this.messages[0]) this.messages[0].classList.add('is-active');
        return;
      }

      this.index = 0;
      this.messages[0].classList.add('is-active');
      const interval = parseInt(this.dataset.interval || '5000', 10);

      this.timer = setInterval(() => {
        this.messages[this.index].classList.remove('is-active');
        this.index = (this.index + 1) % this.messages.length;
        this.messages[this.index].classList.add('is-active');
      }, interval);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }
  }

  customElements.define('announcement-rotator', AnnouncementRotator);

  /* -----------------------------------------------------------------------
     <predictive-search>
     -------------------------------------------------------------------- */

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-search-results]');
      if (!this.input || !this.results) return;

      // The resting state (popular searches, recommended products) lives
      // outside this element, so it can sit anywhere in the drawer.
      const root = this.closest('.drawer__panel') || document;
      this.idle = root.querySelector('[data-search-idle]');

      this.abortController = null;
      this.input.addEventListener('input', debounce(() => this.search(), 280));
    }

    setIdleVisible(visible) {
      if (this.idle) this.idle.hidden = !visible;
    }

    async search() {
      const term = this.input.value.trim();

      if (term.length < 2) {
        this.results.innerHTML = '';
        this.results.hidden = true;
        this.setIdleVisible(true);
        return;
      }

      this.setIdleVisible(false);

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'product,page,article',
        'resources[limit]': '6',
        section_id: 'predictive-search'
      });

      try {
        const response = await fetch(
          `${routes.predictive_search_url || '/search/suggest'}?${params}`,
          { signal: this.abortController.signal }
        );
        if (!response.ok) throw new Error('search failed');

        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const markup = doc.querySelector('[data-predictive-results]');

        this.results.innerHTML = markup ? markup.innerHTML : '';
        this.results.hidden = false;
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.results.hidden = true;
        }
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);

  /* -----------------------------------------------------------------------
     <facet-filters> — filter and sort without a full page reload
     -------------------------------------------------------------------- */

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;

      this.addEventListener('change', (event) => {
        if (!event.target.closest('form')) return;
        this.apply(event.target.closest('form'));
      });

      this.addEventListener('click', (event) => {
        const link = event.target.closest('[data-facet-remove]');
        if (!link) return;
        event.preventDefault();
        this.render(link.href);
      });

      window.addEventListener('popstate', () => this.render(window.location.href, false));
    }

    apply(form) {
      /* Filters and sort live in separate forms; send both so changing one
         keeps the other. */
      const params = new URLSearchParams();
      this.querySelectorAll('form').forEach((each) => {
        new FormData(each).forEach((value, key) => params.append(key, value));
      });
      if (!this.contains(form)) new FormData(form).forEach((value, key) => params.append(key, value));
      // Drop empty values so the URL stays readable and shareable.
      Array.from(params.entries()).forEach(([key, value]) => {
        if (value === '') params.delete(key);
      });
      this.render(`${window.location.pathname}?${params.toString()}`);
    }

    async render(url, pushState = true) {
      const grid = document.querySelector('[data-facet-target]');
      if (grid) grid.classList.add('is-loading');

      try {
        const doc = await fetchSection(url);

        ['[data-facet-target]', '[data-facet-controls]', '[data-facet-active]', '[data-filter-count]'].forEach(
          (selector) => {
            const source = doc.querySelector(selector);
            const destination = document.querySelector(selector);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              if (source.hasAttribute('hidden')) destination.setAttribute('hidden', '');
              else destination.removeAttribute('hidden');
            }
          }
        );

        if (pushState) window.history.pushState({}, '', url);
        initReveal();
      } catch (error) {
        window.location.href = url;
      } finally {
        if (grid) grid.classList.remove('is-loading');
      }
    }
  }

  customElements.define('facet-filters', FacetFilters);

  /* Price range slider: two handles kept in step with the From / To fields.
     Dragging updates the fields; releasing a handle fires the form's
     change, which <facet-filters> applies. The facet markup is replaced
     after each filter, so this listens at the document. */
  const syncPriceRange = (range, source) => {
    const lo = range.querySelector('[data-range-min]');
    const hi = range.querySelector('[data-range-max]');
    const fieldLo = range.querySelector('[data-field-min]');
    const fieldHi = range.querySelector('[data-field-max]');
    const max = parseFloat(hi.max) || 1;
    let a = parseFloat(lo.value) || 0;
    let b = parseFloat(hi.value) || 0;
    if (a > b) {
      if (source === lo) a = b; else b = a;
      lo.value = a;
      hi.value = b;
    }
    fieldLo.value = a > 0 ? a : '';
    fieldHi.value = b < max ? b : '';
    range.style.setProperty('--min', `${(a / max) * 100}%`);
    range.style.setProperty('--max', `${(b / max) * 100}%`);
  };

  document.addEventListener('input', (event) => {
    const thumb = event.target.closest('.price-range__thumb');
    if (thumb) syncPriceRange(thumb.closest('[data-price-range]'), thumb);
  });

  document.addEventListener('change', (event) => {
    const field = event.target.closest('[data-field-min], [data-field-max]');
    if (!field) return;
    const range = field.closest('[data-price-range]');
    const max = parseFloat(range.querySelector('[data-range-max]').max) || 0;
    range.querySelector('[data-range-min]').value = range.querySelector('[data-field-min]').value || 0;
    range.querySelector('[data-range-max]').value = range.querySelector('[data-field-max]').value || max;
    syncPriceRange(range, null);
  }, true);

  /* On phones the sidebar is a drawer from the left: closed by the close
     button, the backdrop or Escape, and the page does not scroll behind it. */
  document.addEventListener('click', (event) => {
    const sidebar = event.target.closest('[data-collection-sidebar]');
    if (!sidebar) return;
    if (event.target.closest('[data-sidebar-close]') || event.target === sidebar) {
      event.preventDefault();
      sidebar.removeAttribute('open');
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-collection-sidebar][open]').forEach((sidebar) => {
      if (window.matchMedia('(max-width: 63.99em)').matches) sidebar.removeAttribute('open');
    });
  });
  document.querySelectorAll('[data-collection-sidebar]').forEach((sidebar) => {
    sidebar.addEventListener('toggle', () => {
      const drawer = window.matchMedia('(max-width: 63.99em)').matches;
      document.documentElement.classList.toggle('has-filter-drawer', drawer && sidebar.open);
    });
  });

  /* -----------------------------------------------------------------------
     Header menu fit

     The merchant controls the menu, so its width is unknown. Each run:
       1. split layout: both halves take the wider half's width (--nav-half)
          so the emblem stays on the true centre;
       2. checks the menu against the room the logo and icons leave over;
       3. if it doesn't fit, tightens gap, then letter spacing, then text
          size, one step at a time, down to a readable minimum;
       4. if it still doesn't fit, switches the header to the menu button
          layout (header--nav-overflow), or with "hide" drops the last
          links and shows the menu button beside them (header--nav-partial).
     Nothing is ever drawn under the icons.
     -------------------------------------------------------------------- */

  const NAV_FIT_STEPS = [
    { gap: 1, track: 1, size: 1 },
    { gap: 0.75, track: 0.6, size: 1 },
    { gap: 0.6, track: 0.3, size: 0.94 },
    { gap: 0.5, track: 0, size: 0.88 },
    { gap: 0.42, track: 0, size: 0.8 },
    { gap: 0.36, track: 0, size: 0.72 }
  ];

  const setNavFit = (header, step) => {
    header.style.setProperty('--nav-fit-gap', step.gap);
    header.style.setProperty('--nav-fit-track', step.track);
    header.style.setProperty('--nav-fit-size', step.size);
  };

  // Remembers each fit by window width (sessionStorage), so the inline
  // script in header.liquid can apply it before first paint next page.
  const NAV_CACHE = 'mb-navfit';

  const saveNavFit = (header, state) => {
    try {
      const cache = JSON.parse(sessionStorage.getItem(NAV_CACHE) || '{}');
      const key = `${header.dataset.navKey || ''}@${window.innerWidth}`;
      delete cache[key];
      cache[key] = state;
      const keys = Object.keys(cache);
      keys.slice(0, Math.max(0, keys.length - 40)).forEach((old) => delete cache[old]);
      sessionStorage.setItem(NAV_CACHE, JSON.stringify(cache));
    } catch (error) {
      /* Private mode or storage full: the menu still fits, it just fades in. */
    }
  };

  const fitHeaderNav = (header) => {
    const nav = header.querySelector('.header__nav');
    const logo = header.querySelector('.header__logo');
    const actions = header.querySelector('.header__actions');
    if (!nav || !logo || !actions) return;

    const split = header.classList.contains('header--split');
    const lists = split
      ? [nav.querySelector('.nav--left'), nav.querySelector('.nav--right')].filter(Boolean)
      : [nav.querySelector('.nav')].filter(Boolean);

    // All of this runs synchronously, so the reset below is never painted.
    header.classList.remove('header--nav-overflow', 'header--nav-partial');
    nav.querySelectorAll('.nav__item--hidden').forEach((item) => item.classList.remove('nav__item--hidden'));
    nav.style.removeProperty('--nav-half');
    setNavFit(header, NAV_FIT_STEPS[0]);

    // Below these widths the desktop menu is hidden by CSS anyway.
    if (!lists.length || !window.matchMedia(split ? '(min-width: 990px)' : '(min-width: 48em)').matches) return;

    const px = (value) => parseFloat(value) || 0;
    const headerStyle = getComputedStyle(header);
    const room = header.clientWidth - px(headerStyle.paddingInlineStart) - px(headerStyle.paddingInlineEnd);
    let half = 0;

    const fits = () => {
      const gap = px(getComputedStyle(header).columnGap);
      const logoWidth = logo.offsetWidth + px(getComputedStyle(logo).marginInlineStart);
      if (split) {
        nav.style.removeProperty('--nav-half');
        half = Math.ceil(Math.max(...lists.map((list) => list.scrollWidth)));
        nav.style.setProperty('--nav-half', `${half}px`);
        const emblem = nav.querySelector('.header__emblem');
        const navGap = px(getComputedStyle(nav).columnGap);
        const menu = half * 2 + (emblem ? emblem.offsetWidth : 0) + navGap * 2;
        // The outer columns are equal (1fr each) to keep the menu centred,
        // so each must hold the wider of the logo and the icon cluster.
        return menu + Math.max(logoWidth, actions.offsetWidth) * 2 + gap * 2 <= room + 0.5;
      }
      return logoWidth + lists[0].scrollWidth + actions.offsetWidth + gap * 2 <= room + 0.5;
    };

    for (let s = 0; s < NAV_FIT_STEPS.length; s += 1) {
      setNavFit(header, NAV_FIT_STEPS[s]);
      if (fits()) {
        saveNavFit(header, { s, h: split ? half : 0, m: '', x: 0 });
        return;
      }
    }

    if (header.dataset.navOverflow === 'hide') {
      header.classList.add('header--nav-partial');
      const items = lists.flatMap((list) => Array.from(list.children));
      for (let i = items.length - 1; i > 0; i -= 1) {
        items[i].classList.add('nav__item--hidden');
        if (fits()) {
          saveNavFit(header, {
            s: NAV_FIT_STEPS.length - 1,
            h: split ? half : 0,
            m: 'header--nav-partial',
            x: items.length - i
          });
          return;
        }
      }
      header.classList.remove('header--nav-partial');
      nav.querySelectorAll('.nav__item--hidden').forEach((item) => item.classList.remove('nav__item--hidden'));
    }

    nav.style.removeProperty('--nav-half');
    setNavFit(header, NAV_FIT_STEPS[0]);
    header.classList.add('header--nav-overflow');
    saveNavFit(header, { s: 0, h: 0, m: 'header--nav-overflow', x: 0 });
  };

  const balanceNav = () => document.querySelectorAll('[data-header]').forEach(fitHeaderNav);

  // The menu stays hidden (CSS, until data-nav-ready) until it has been
  // measured with the real fonts; measuring with the fallback font and
  // re-fitting once the brand font arrives is what made it jump.
  const revealNav = () =>
    document.querySelectorAll('[data-header]:not([data-nav-ready])').forEach((header) => header.setAttribute('data-nav-ready', ''));

  const fontsSettled = () => {
    if (!document.fonts || document.fonts.status === 'loaded') return Promise.resolve();
    return Promise.race([document.fonts.ready, new Promise((resolve) => setTimeout(resolve, 1200))]);
  };

  fontsSettled().then(() => {
    balanceNav();
    revealNav();
  });

  // Resizing: re-fit once per frame, so the menu never sits over the icons
  // for a debounce interval and steps change in time with the drag.
  let navFrame = 0;
  const balanceNavFrame = () => {
    if (navFrame) return;
    navFrame = requestAnimationFrame(() => {
      navFrame = 0;
      balanceNav();
    });
  };

  window.addEventListener('resize', balanceNavFrame);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(balanceNav);
  window.addEventListener('load', balanceNav);
  document.addEventListener('shopify:section:load', () =>
    requestAnimationFrame(() => {
      balanceNav();
      revealNav();
    })
  );

  // Icons added later (an app's wishlist button, a cart count) or a logo
  // that loads late change the room available; re-measure when they do.
  if ('ResizeObserver' in window) {
    const widths = new WeakMap();
    const observer = new ResizeObserver((entries) => {
      const changed = entries.some((entry) => {
        const width = Math.round(entry.contentRect.width);
        const before = widths.get(entry.target);
        widths.set(entry.target, width);
        return before !== undefined && before !== width;
      });
      if (changed) balanceNavFrame();
    });
    document
      .querySelectorAll('[data-header] .header__actions, [data-header] .header__logo')
      .forEach((el) => observer.observe(el));
  }

  /* The collection sidebar starts folded on phones. */
  document.querySelectorAll('[data-collection-sidebar]').forEach((sidebar) => {
    if (window.matchMedia('(max-width: 63.99em)').matches) sidebar.removeAttribute('open');
  });

  /* -----------------------------------------------------------------------
     <sticky-atc> — mobile add to cart bar
     -------------------------------------------------------------------- */

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const anchor = document.querySelector(this.dataset.watch || '[data-atc-anchor]');
      if (!anchor || !('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
          });
        },
        { threshold: 0 }
      );

      observer.observe(anchor);
    }
  }

  customElements.define('sticky-atc', StickyAtc);

  /* -----------------------------------------------------------------------
     Share button
     -------------------------------------------------------------------- */

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-share]');
    if (!button) return;
    event.preventDefault();

    const url = button.dataset.share || window.location.href;
    const title = button.dataset.shareTitle || document.title;

    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (error) {
        if (error.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      const feedback = button.querySelector('[data-share-feedback]');
      if (feedback) {
        feedback.hidden = false;
        setTimeout(() => {
          feedback.hidden = true;
        }, 2200);
      }
    } catch (error) {
      /* Clipboard unavailable — nothing useful to do. */
    }
  });

  /* -----------------------------------------------------------------------
     Accordions — keep only one open per group when asked
     -------------------------------------------------------------------- */

  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;

    const group = details.closest('[data-accordion-exclusive]');
    if (!group) return;

    group.querySelectorAll('details[open]').forEach((other) => {
      if (other !== details) other.open = false;
    });
  }, true);

  /* -----------------------------------------------------------------------
     <video-lightbox> — opens a clip full screen on click

     Each trigger carries its player in an inert <template>, so a row of
     eight clips loads nothing until one is actually opened. Closing empties
     the target, which stops playback and frees the stream.
     -------------------------------------------------------------------- */

  class VideoLightbox extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector('[data-lightbox-modal]');
      this.target = this.querySelector('[data-lightbox-target]');
      if (!this.modal || !this.target) return;

      this.releaseFocus = null;

      this.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-lightbox-open]');
        if (trigger && this.contains(trigger)) {
          event.preventDefault();
          this.open(trigger);
          return;
        }

        if (event.target.closest('[data-lightbox-close]')) {
          event.preventDefault();
          this.close();
        }
      });

      this.onKeydown = (event) => {
        if (event.key === 'Escape' && this.modal.classList.contains('is-open')) this.close();
      };
      document.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
    }

    open(trigger) {
      const template = trigger.querySelector('template[data-lightbox-content]');
      if (!template) return;

      this.target.innerHTML = '';
      this.target.appendChild(template.content.cloneNode(true));

      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      lockScroll();
      this.releaseFocus = trapFocus(this.modal, trigger);
    }

    close() {
      if (!this.modal.classList.contains('is-open')) return;

      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      // Emptying the target stops playback rather than leaving audio running.
      this.target.innerHTML = '';
      unlockScroll();
      if (this.releaseFocus) this.releaseFocus();
      this.releaseFocus = null;
    }
  }

  customElements.define('video-lightbox', VideoLightbox);

  /* -----------------------------------------------------------------------
     <hls-video> — plays a direct video file, including Shopify CDN URLs

     Safari and iOS play .m3u8 natively. Other browsers need a shim, which is
     fetched only when an HLS source is actually present and unsupported — so
     pages using .mp4 or a platform embed never pay for it.
     -------------------------------------------------------------------- */

  const HLS_SHIM = 'https://cdn.jsdelivr.net/npm/hls.js@1/dist/hls.min.js';

  class HlsVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;

      const src = this.video.getAttribute('src');
      const isHls = (this.video.dataset.type || '').indexOf('mpeg') !== -1;
      if (!src || !isHls) return;

      // Safari reports playability for HLS and needs nothing further.
      if (this.video.canPlayType('application/vnd.apple.mpegurl')) return;

      this.load(src);
    }

    load(src) {
      const attach = () => {
        if (!window.Hls || !window.Hls.isSupported()) return;
        const hls = new window.Hls({ capLevelToPlayerSize: true });
        hls.loadSource(src);
        hls.attachMedia(this.video);
        this.hls = hls;
      };

      if (window.Hls) {
        attach();
        return;
      }

      if (!document.querySelector('script[data-hls-shim]')) {
        const script = document.createElement('script');
        script.src = HLS_SHIM;
        script.defer = true;
        script.dataset.hlsShim = 'true';
        document.head.appendChild(script);
      }

      const existing = document.querySelector('script[data-hls-shim]');
      existing.addEventListener('load', attach, { once: true });
    }

    disconnectedCallback() {
      if (this.hls) this.hls.destroy();
    }
  }

  customElements.define('hls-video', HlsVideo);

  /* -----------------------------------------------------------------------
     <wishlist-button> — saved items

     Shopify has no native wishlist, so this keeps a list of product ids in
     localStorage. It survives page loads on the same device but is not tied
     to a customer account; swapping to an app later only means changing this
     class, since the markup stays the same.
     -------------------------------------------------------------------- */

  const WISHLIST_KEY = 'mb-wishlist';

  const wishlist = {
    read() {
      let raw = [];
      try {
        raw = JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
      } catch (error) {
        return [];
      }
      // Older entries were bare id strings; the handle is needed to look the
      // product up, so normalise both shapes.
      return raw
        .map((entry) =>
          typeof entry === 'string' ? { id: entry, handle: '' } : entry
        )
        .filter((entry) => entry && entry.id);
    },
    write(list) {
      try {
        localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
      } catch (error) {
        /* Storage blocked — the toggle still works for this page view. */
      }
    },
    has(id) {
      return this.read().some((entry) => entry.id === String(id));
    },
    toggle(id, handle) {
      const list = this.read();
      const key = String(id);
      const at = list.findIndex((entry) => entry.id === key);
      if (at === -1) list.push({ id: key, handle: handle || '' });
      else list.splice(at, 1);
      this.write(list);
      return at === -1;
    },
    count() {
      return this.read().length;
    }
  };

  function syncWishlistCounts() {
    const count = wishlist.count();
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;

      this.id = this.button.dataset.wishlist;
      this.sync(wishlist.has(this.id));

      // Another heart for the same product (card, drawer) may change it.
      if (!this.onListChange) {
        this.onListChange = () => {
          if (this.isConnected) this.sync(wishlist.has(this.id));
        };
        document.addEventListener('wishlist:change', this.onListChange);
      }

      this.button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const saved = wishlist.toggle(this.id, this.button.dataset.wishlistHandle);
        this.sync(saved);
        syncWishlistCounts();
        document.dispatchEvent(
          new CustomEvent('wishlist:change', {
            bubbles: true,
            detail: { id: this.id, saved: saved, count: wishlist.read().length }
          })
        );
      });
    }

    sync(saved) {
      this.button.setAttribute('aria-pressed', String(saved));
      this.classList.toggle('is-saved', saved);
      const off = this.querySelector('[data-wishlist-off]');
      const on = this.querySelector('[data-wishlist-on]');
      if (off) off.hidden = saved;
      if (on) on.hidden = !saved;
    }
  }

  customElements.define('wishlist-button', WishlistButton);
  window.MrBeardWishlist = wishlist;

  /* -----------------------------------------------------------------------
     <bundle-builder> — pick several products, add them in one request

     The total and discount shown are a calculation for the shopper. Shopify
     prices the real cart, so the saving itself comes from an automatic
     discount or the code configured on the section.
     -------------------------------------------------------------------- */

  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      this.minItems = parseInt(this.dataset.minItems, 10) || 0;
      this.percent = parseFloat(this.dataset.discount) || 0;
      this.code = (this.dataset.discountCode || '').trim();
      this.openDrawer = this.dataset.openDrawer !== 'false';

      this.countEl = this.querySelector('[data-bundle-count]');
      this.discountEl = this.querySelector('[data-bundle-discount]');
      this.totalEl = this.querySelector('[data-bundle-total]');
      this.hintEl = this.querySelector('[data-bundle-hint]');
      this.errorEl = this.querySelector('[data-bundle-error]');
      this.cta = this.querySelector('[data-bundle-add]');

      this.hintTemplate = this.hintEl ? this.hintEl.textContent.trim() : '';
      this.selected = new Map();

      this.picker = this.querySelector('[data-bundle-picker]');
      if (this.picker) {
        this.picker.addEventListener('click', (event) => {
          if (event.target === this.picker || event.target.closest('[data-bundle-picker-close]')) {
            this.picker.close();
          }
        });
        this.picker.querySelector('[data-bundle-picker-confirm]').addEventListener('click', () => this.confirmPick());
      }

      this.addEventListener('click', (event) => {
        let toggle = event.target.closest('[data-bundle-item]');
        // Anywhere on a selectable card selects it, as long as the click
        // wasn't on its own link (the title still goes to the product).
        if (!toggle) {
          const card = event.target.closest('[data-bundle-card]');
          if (card && !event.target.closest('a')) toggle = card.querySelector('[data-bundle-item]');
        }
        if (toggle) {
          event.preventDefault();
          this.toggle(toggle);
          return;
        }
        if (event.target.closest('[data-bundle-add]')) {
          event.preventDefault();
          this.submit();
        }
      });

      this.update();
    }

    toggle(button) {
      const id = button.dataset.variantId;
      if (id && this.selected.has(id)) {
        this.selected.delete(id);
      } else if (button.hasAttribute('data-needs-choice')) {
        // Products with options pick a variant in the popup first.
        this.openPicker(button);
        return;
      } else {
        this.selected.set(id, parseInt(button.dataset.price, 10) || 0);
      }
      this.paint(button);
    }

    paint(button) {
      const id = button.dataset.variantId;

      const on = this.selected.has(id);
      button.setAttribute('aria-pressed', String(on));
      button.classList.toggle('is-selected', on);
      button.closest('.bcard').classList.toggle('is-selected', on);
      const add = button.querySelector('[data-add-text]');
      const remove = button.querySelector('[data-remove-text]');
      if (add) add.hidden = on;
      if (remove) remove.hidden = !on;

      this.update();
    }

    openPicker(button) {
      const card = button.closest('.bcard');
      const source = card && card.querySelector('[data-bundle-variants]');
      if (!this.picker || !source) return;

      const data = JSON.parse(source.textContent);
      // Values per option, in the order the variants list them.
      data.values = data.options.map((_, i) => [...new Set(data.variants.map((v) => v.options[i]))]);
      const start = data.variants.find((v) => v.available) || data.variants[0];
      this.pick = { button: button, card: card, data: data, choice: start.options.slice() };

      const image = this.picker.querySelector('[data-bundle-picker-image]');
      image.hidden = !data.image;
      if (data.image) image.src = data.image;
      setPickPlate(
        image,
        data.image,
        card.style.getPropertyValue('--pbg-l').trim(),
        card.style.getPropertyValue('--pbg-d').trim()
      );
      this.picker.querySelector('[data-bundle-picker-title]').textContent = data.title;

      this.renderPick();
      this.picker.showModal();
    }

    renderPick() {
      const { data, choice } = this.pick;
      const wrap = this.picker.querySelector('[data-bundle-picker-options]');
      wrap.innerHTML = '';

      data.options.forEach((name, i) => {
        const group = document.createElement('fieldset');
        group.className = 'bpick__group';
        const legend = document.createElement('legend');
        legend.className = 'bpick__legend';
        legend.textContent = `${name}: ${choice[i]}`;
        group.appendChild(legend);

        data.values[i].forEach((value) => {
          const test = choice.slice();
          test[i] = value;
          const match = data.variants.find((v) => v.options.every((o, k) => o === test[k]));
          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'bpick__value';
          chip.textContent = value;
          chip.setAttribute('aria-pressed', String(choice[i] === value));
          chip.classList.toggle('is-unavailable', !match || !match.available);
          chip.addEventListener('click', () => {
            choice[i] = value;
            this.renderPick();
          });
          group.appendChild(chip);
        });
        wrap.appendChild(group);
      });

      const variant = this.currentPick();
      const ok = Boolean(variant && variant.available);
      const confirm = this.picker.querySelector('[data-bundle-picker-confirm]');
      confirm.disabled = !ok;
      confirm.querySelector('[data-label-add]').hidden = !ok;
      confirm.querySelector('[data-label-soldout]').hidden = ok;
      this.picker.querySelector('[data-bundle-picker-price]').textContent = variant ? formatMoney(variant.price) : '';
    }

    currentPick() {
      const { data, choice } = this.pick;
      return data.variants.find((v) => v.options.every((o, k) => o === choice[k]));
    }

    confirmPick() {
      const variant = this.currentPick();
      if (!variant || !variant.available) return;
      const { button, card } = this.pick;

      button.dataset.variantId = String(variant.id);
      button.dataset.price = String(variant.price);
      this.selected.set(button.dataset.variantId, variant.price);

      const price = card.querySelector('[data-bundle-price]');
      if (price) price.textContent = formatMoney(variant.price);
      const subtitle = card.querySelector('[data-bundle-subtitle]');
      if (subtitle) subtitle.textContent = variant.title;

      this.picker.close();
      this.paint(button);
    }

    update() {
      const count = this.selected.size;
      let subtotal = 0;
      this.selected.forEach((price) => {
        subtotal += price;
      });

      const qualifies = this.minItems > 0 && count >= this.minItems;
      const percent = qualifies ? this.percent : 0;
      const total = Math.round(subtotal * (1 - percent / 100));

      // Two-digit count and a bare "0" until the discount applies, as the
      // artwork's kit panel shows them.
      if (this.countEl) this.countEl.textContent = String(count).padStart(2, '0');
      if (this.discountEl) this.discountEl.textContent = percent ? `${percent}%` : '0';
      if (this.totalEl) this.totalEl.textContent = formatMoney(total);
      if (this.cta) this.cta.disabled = count === 0;

      if (this.hintEl) {
        if (qualifies) {
          this.hintEl.textContent = this.hintEl.dataset.unlocked || '';
          this.hintEl.hidden = !this.hintEl.textContent;
          this.hintEl.classList.add('is-unlocked');
        } else {
          const remaining = Math.max(0, this.minItems - count);
          this.hintEl.textContent = this.hintTemplate.replace(/\d+/, remaining || this.minItems);
          this.hintEl.hidden = false;
          this.hintEl.classList.remove('is-unlocked');
        }
      }

      this.classList.toggle('has-selection', count > 0);
    }

    async submit() {
      if (!this.selected.size || !this.cta) return;

      this.cta.classList.add('is-loading');
      this.cta.disabled = true;
      if (this.errorEl) this.errorEl.hidden = true;

      const items = [...this.selected.keys()].map((id) => ({ id: id, quantity: 1 }));

      try {
        const data = await Cart.request(`${routes.cart_add_url || '/cart/add'}.js`, {
          items: items,
          sections: Cart.sectionsToRender().join(','),
          sections_url: window.location.pathname
        });

        Cart.applySections(data.sections);

        // Record the code on the cart in place (Shopify's cart API takes a
        // discount); fall back to the /discount/ URL if that's refused.
        if (this.code) {
          try {
            const coded = await Cart.request(`${routes.cart_update_url || '/cart/update'}.js`, {
              discount: this.code,
              sections: Cart.sectionsToRender().join(','),
              sections_url: window.location.pathname
            });
            Cart.applySections(coded.sections);
          } catch (codeError) {
            const back = window.location.pathname + window.location.search;
            window.location.href =
              '/discount/' + encodeURIComponent(this.code) + '?redirect=' + encodeURIComponent(back);
            return;
          }
        }

        const drawer = document.getElementById('cart-drawer');
        if (this.openDrawer && drawer && config.cartType === 'drawer') drawer.open(this.cta);
      } catch (error) {
        if (this.errorEl) {
          this.errorEl.textContent =
            (error && (error.description || error.message)) || strings.cartError || '';
          this.errorEl.hidden = false;
        }
      } finally {
        this.cta.classList.remove('is-loading');
        this.cta.disabled = false;
      }
    }
  }

  customElements.define('bundle-builder', BundleBuilder);

  /* -----------------------------------------------------------------------
     <kit-add> — adds a whole curated kit in one request

     A normal form post cannot carry several line items, so this goes through
     the cart API and falls back to the cart page if that fails.
     -------------------------------------------------------------------- */

  class KitAdd extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-kit-add]');
      if (!this.button) return;

      this.variants = (this.dataset.variants || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
      this.code = (this.dataset.discountCode || '').trim();

      if (!this.variants.length) return;

      this.button.addEventListener('click', () => this.add());
    }

    async add() {
      this.button.classList.add('is-loading');
      this.button.disabled = true;

      try {
        const data = await Cart.request(`${routes.cart_add_url || '/cart/add'}.js`, {
          items: this.variants.map((id) => ({ id: id, quantity: 1 })),
          sections: Cart.sectionsToRender().join(','),
          sections_url: window.location.pathname
        });

        Cart.applySections(data.sections);

        if (this.code) {
          const back = window.location.pathname + window.location.search;
          window.location.href =
            '/discount/' + encodeURIComponent(this.code) + '?redirect=' + encodeURIComponent(back);
          return;
        }

        const drawer = document.getElementById('cart-drawer');
        if (drawer && config.cartType === 'drawer') drawer.open(this.button);
      } catch (error) {
        window.location.href = routes.cart_url || '/cart';
      } finally {
        this.button.classList.remove('is-loading');
        this.button.disabled = false;
      }
    }
  }

  customElements.define('kit-add', KitAdd);

  /* -----------------------------------------------------------------------
     Social clips play muted on a loop with no controls; the speaker button
     turns sound on for that clip and mutes any other one.
     -------------------------------------------------------------------- */

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-ugc-sound]');
    if (!button) return;
    const video = button.parentElement.querySelector('video');
    if (!video) return;
    const turnOn = video.muted;
    document.querySelectorAll('[data-ugc-sound]').forEach((other) => {
      const otherVideo = other.parentElement.querySelector('video');
      if (otherVideo) otherVideo.muted = true;
      other.setAttribute('aria-pressed', 'false');
    });
    video.muted = !turnOn;
    if (turnOn) {
      video.play().catch(() => {});
      button.setAttribute('aria-pressed', 'true');
    }
  });

  /* -----------------------------------------------------------------------
     <fbt-bundle> — frequently bought together

     Ticking rows re-prices the bundle live and the button adds exactly what is
     ticked. The first row tracks the variant picker, so a scent chosen higher
     up the page is the one that gets added.
     -------------------------------------------------------------------- */

  class FbtBundle extends HTMLElement {
    connectedCallback() {
      this.inputs = [...this.querySelectorAll('[data-fbt-item]')];
      this.button = this.querySelector('[data-fbt-add]');
      if (!this.inputs.length || !this.button) return;

      this.wasEl = this.querySelector('[data-fbt-was]');
      this.nowEl = this.querySelector('[data-fbt-now]');
      this.badgeEl = this.querySelector('[data-fbt-badge]');
      this.mainInput = this.querySelector('[data-fbt-item][data-main]');
      this.mainPriceEl = this.querySelector('[data-fbt-row-price]');

      this.discountType = this.dataset.discountType || 'none';
      this.percent = parseFloat(this.dataset.discountPercent) || 0;
      this.amount = parseInt(this.dataset.discountAmount, 10) || 0;
      this.code = (this.dataset.discountCode || '').trim();
      this.badgeTemplate = this.dataset.badgeTemplate || '';

      /* A locked row stays ticked, so the click is swallowed before the
         checkbox toggles rather than being toggled back afterwards. */
      this.inputs.forEach((input) => {
        if (input.hasAttribute('data-locked')) {
          input.addEventListener('click', (event) => event.preventDefault());
        }
      });

      this.addEventListener('change', () => this.update());
      this.button.addEventListener('click', () => this.add());

      /* The picker is in a different section, so the event is caught as it
         passes through the document rather than from a shared ancestor. */
      if (this.mainInput) {
        document.addEventListener('variant:change', (event) => this.syncMain(event.detail.variant));
      }

      this.update();
    }

    syncMain(variant) {
      if (!variant) return;
      const thumb = this.querySelector(`[data-fbt-thumb="${this.mainInput.dataset.id}"]`);
      if (thumb) thumb.dataset.fbtThumb = variant.id;

      this.mainInput.dataset.id = variant.id;
      if (typeof variant.price === 'number') {
        this.mainInput.dataset.price = variant.price;
        if (this.mainPriceEl) this.mainPriceEl.textContent = formatMoney(variant.price);
      }
      this.update();
    }

    get chosen() {
      return this.inputs.filter((input) => input.checked);
    }

    saving(subtotal) {
      if (this.discountType === 'percent') return Math.round((subtotal * this.percent) / 100);
      if (this.discountType === 'amount') return Math.min(this.amount, subtotal);
      return 0;
    }

    update() {
      const chosen = this.chosen;
      const subtotal = chosen.reduce(
        (sum, input) => sum + (parseInt(input.dataset.price, 10) || 0),
        0
      );

      /* A saving only makes sense once the bundle is actually a bundle. */
      const saving = chosen.length > 1 ? this.saving(subtotal) : 0;
      const total = subtotal - saving;

      if (this.nowEl) this.nowEl.textContent = formatMoney(total);

      if (this.wasEl) {
        this.wasEl.textContent = formatMoney(subtotal);
        this.wasEl.hidden = saving <= 0;
      }

      if (this.badgeEl) {
        this.badgeEl.textContent = this.badgeTemplate.replace('[amount]', formatMoney(saving));
        this.badgeEl.hidden = saving <= 0 || !this.badgeTemplate;
      }

      this.inputs.forEach((input) => {
        const off = !input.checked;
        const row = input.closest('.fbt__row');
        if (row) row.classList.toggle('is-off', off);
        const thumb = this.querySelector(`[data-fbt-thumb="${input.dataset.id}"]`);
        if (thumb) thumb.classList.toggle('is-off', off);
      });

      this.button.disabled = chosen.length === 0;
    }

    async add() {
      const items = this.chosen
        .map((input) => ({ id: input.dataset.id, quantity: 1 }))
        .filter((item) => item.id);
      if (!items.length) return;

      this.button.classList.add('is-loading');
      this.button.disabled = true;

      const url = `${routes.cart_add_url || '/cart/add'}.js`;
      const payload = (list) => ({
        items: list,
        sections: Cart.sectionsToRender().join(','),
        sections_url: window.location.pathname
      });

      try {
        let data = null;
        try {
          data = await Cart.request(url, payload(items));
        } catch (bundleError) {
          /* Shopify rejects the whole request if one item cannot be added
             (sold out, limited stock), so add them one at a time and keep
             whatever succeeds. */
          for (const item of items) {
            try {
              data = await Cart.request(url, payload([item]));
            } catch (itemError) {
              /* skipped */
            }
          }
          if (!data) throw bundleError;
        }

        if (data && data.sections) Cart.applySections(data.sections);
        this.showError('');

        if (this.code) {
          const back = window.location.pathname + window.location.search;
          window.location.href =
            '/discount/' + encodeURIComponent(this.code) + '?redirect=' + encodeURIComponent(back);
          return;
        }

        const drawer = document.getElementById('cart-drawer');
        if (drawer && config.cartType === 'drawer') {
          drawer.open(this.button);
        } else {
          window.location.href = routes.cart_url || '/cart';
        }
      } catch (error) {
        this.showError((error && (error.description || error.message)) || strings.unavailable || 'Could not add to cart');
      } finally {
        this.button.classList.remove('is-loading');
        this.button.disabled = false;
      }
    }
  }

  FbtBundle.prototype.showError = function (message) {
    let el = this.querySelector('[data-fbt-error]');
    if (!el && message) {
      el = document.createElement('p');
      el.className = 'fbt__error';
      el.setAttribute('data-fbt-error', '');
      el.setAttribute('role', 'alert');
      this.button.insertAdjacentElement('afterend', el);
    }
    if (el) {
      el.textContent = message;
      el.hidden = !message;
    }
  };

  customElements.define('fbt-bundle', FbtBundle);

  /* -----------------------------------------------------------------------
     Store-wide scent picker — any [data-quick-pick] button whose parent
     holds a [data-quick-pick-data] script opens the popup; the chosen
     variant goes straight to the cart and the drawer opens.
     -------------------------------------------------------------------- */

  /* The picker's image plate: shown only with an image, and painted with the
     product's own background (custom.background_color / _dark metafields)
     when the data carries one, otherwise the global --product-media-bg. */
  function setPickPlate(image, hasImage, light, dark) {
    const plate = image.closest('[data-pick-plate]');
    if (!plate) return;
    plate.hidden = !hasImage;
    const l = light || dark || '';
    const d = dark || light || '';
    plate.classList.toggle('has-product-bg', Boolean(l));
    if (l) {
      plate.style.setProperty('--pbg-l', l);
      plate.style.setProperty('--pbg-d', d);
    } else {
      plate.style.removeProperty('--pbg-l');
      plate.style.removeProperty('--pbg-d');
    }
  }

  const QuickPick = {
    dialog: null,
    state: null,

    init() {
      this.dialog = document.querySelector('[data-quick-pick-dialog]');
      if (!this.dialog) return;
      document.addEventListener('click', (event) => {
        const button = event.target.closest('[data-quick-pick]');
        if (!button) return;
        const source = button.parentElement.querySelector('[data-quick-pick-data]');
        if (!source) return;
        event.preventDefault();
        this.open(button, JSON.parse(source.textContent));
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog || event.target.closest('[data-quick-pick-close]')) this.dialog.close();
      });
      this.dialog.querySelector('[data-quick-pick-confirm]').addEventListener('click', () => this.confirm());
    },

    open(button, data) {
      data.values = data.options.map((_, i) => [...new Set(data.variants.map((v) => v.options[i]))]);
      const start = data.variants.find((v) => v.available) || data.variants[0];
      this.state = { button: button, data: data, choice: start.options.slice() };
      const image = this.dialog.querySelector('[data-quick-pick-image]');
      image.hidden = !data.image;
      if (data.image) image.src = data.image;
      setPickPlate(image, data.image, data.bg, data.bgDark);
      this.dialog.querySelector('[data-quick-pick-title]').textContent = data.title;
      this.dialog.querySelector('[data-quick-pick-error]').hidden = true;
      this.render();
      this.dialog.showModal();
    },

    current() {
      const { data, choice } = this.state;
      return data.variants.find((v) => v.options.every((o, k) => o === choice[k]));
    },

    render() {
      const { data, choice } = this.state;
      const wrap = this.dialog.querySelector('[data-quick-pick-options]');
      wrap.innerHTML = '';
      data.options.forEach((name, i) => {
        const group = document.createElement('fieldset');
        group.className = 'bpick__group';
        const legend = document.createElement('legend');
        legend.className = 'bpick__legend';
        legend.textContent = `${name}: ${choice[i]}`;
        group.appendChild(legend);
        data.values[i].forEach((value) => {
          const test = choice.slice();
          test[i] = value;
          const match = data.variants.find((v) => v.options.every((o, k) => o === test[k]));
          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'bpick__value';
          chip.textContent = value;
          chip.setAttribute('aria-pressed', String(choice[i] === value));
          chip.classList.toggle('is-unavailable', !match || !match.available);
          chip.addEventListener('click', () => {
            choice[i] = value;
            this.render();
          });
          group.appendChild(chip);
        });
        wrap.appendChild(group);
      });
      const variant = this.current();
      const ok = Boolean(variant && variant.available);
      const confirm = this.dialog.querySelector('[data-quick-pick-confirm]');
      confirm.disabled = !ok;
      confirm.querySelector('[data-label-add]').hidden = !ok;
      confirm.querySelector('[data-label-soldout]').hidden = ok;
      this.dialog.querySelector('[data-quick-pick-price]').textContent = variant ? formatMoney(variant.price) : '';
    },

    async confirm() {
      const variant = this.current();
      if (!variant || !variant.available) return;
      const confirm = this.dialog.querySelector('[data-quick-pick-confirm]');
      confirm.classList.add('is-loading');
      confirm.disabled = true;
      try {
        const data = await Cart.request(`${routes.cart_add_url || '/cart/add'}.js`, {
          items: [{ id: variant.id, quantity: 1 }],
          sections: Cart.sectionsToRender().join(','),
          sections_url: window.location.pathname
        });
        if (data && data.sections) Cart.applySections(data.sections);
        this.dialog.close();
        const drawer = document.getElementById('cart-drawer');
        if (drawer && config.cartType === 'drawer') {
          drawer.open(this.state.button);
        } else {
          window.location.href = routes.cart_url || '/cart';
        }
      } catch (error) {
        const box = this.dialog.querySelector('[data-quick-pick-error]');
        box.textContent = (error && (error.description || error.message)) || strings.unavailable || '';
        box.hidden = false;
      } finally {
        confirm.classList.remove('is-loading');
        confirm.disabled = false;
      }
    }
  };

  QuickPick.init();

  /* -----------------------------------------------------------------------
     <store-locator> — list of stores beside a Leaflet map with free OpenStreetMap
     tiles (tinted light or dark with the theme mode in CSS). Leaflet loads
     after this script, so the map starts once window.L exists.
     -------------------------------------------------------------------- */

  class StoreLocator extends HTMLElement {
    connectedCallback() {
      this.mapEl = this.querySelector('[data-map]');
      this.stores = [...this.querySelectorAll('[data-store]')];
      if (!this.mapEl) return;
      let tries = 0;
      const wait = () => {
        if (window.L) return this.init();
        tries += 1;
        if (tries < 100) setTimeout(wait, 100);
      };
      wait();
    }

    tiles() {
      /* Free OpenStreetMap tiles, no key; tinted to the theme in CSS. */
      return 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
    }

    init() {
      const L = window.L;
      const lat = parseFloat(this.dataset.lat) || 30.0444;
      const lng = parseFloat(this.dataset.lng) || 31.2357;
      this.map = L.map(this.mapEl, { scrollWheelZoom: false, zoomControl: true }).setView([lat, lng], parseInt(this.dataset.zoom, 10) || 11);
      this.layer = L.tileLayer(this.tiles(), {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(this.map);

      this.markers = [];
      const points = [];
      this.stores.forEach((store, i) => {
        const sLat = parseFloat(store.dataset.lat);
        const sLng = parseFloat(store.dataset.lng);
        if (Number.isNaN(sLat) || Number.isNaN(sLng)) return;
        const icon = L.divIcon({
          className: 'locator__pin-wrap',
          html: `<span class="locator__pin"><span>${i + 1}</span></span>`,
          iconSize: [38, 46],
          iconAnchor: [19, 46],
          popupAnchor: [0, -42]
        });
        const marker = L.marker([sLat, sLng], { icon: icon, title: store.dataset.name }).addTo(this.map);
        marker.bindPopup(`<strong>${store.dataset.name}</strong>`);
        marker.on('click', () => this.select(i, false));
        this.markers[i] = marker;
        points.push([sLat, sLng]);
        store.querySelector('[data-store-focus]').addEventListener('click', () => this.select(i, true));
      });
      if (points.length > 1) this.map.fitBounds(points, { padding: [48, 48] });
      else if (points.length === 1) this.map.setView(points[0], 14);

      setTimeout(() => this.map.invalidateSize(), 200);
    }

    select(i, fly) {
      this.stores.forEach((store, k) => store.classList.toggle('is-active', k === i));
      const marker = this.markers[i];
      if (!marker) return;
      if (fly) this.map.flyTo(marker.getLatLng(), 15, { duration: 0.8 });
      marker.openPopup();
      if (!fly) this.stores[i].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  customElements.define('store-locator', StoreLocator);

  /* -----------------------------------------------------------------------
     Routine quiz popup ([data-quiz]) — one question at a time; answers add
     their products to a tally and the most-picked ones are recommended with
     add to cart, the scent popup for products with options, and add all.
     -------------------------------------------------------------------- */

  class RoutineQuiz {
    constructor(dialog) {
      this.dialog = dialog;
      this.data = JSON.parse(dialog.querySelector('[data-quiz-data]').textContent);
      this.step = dialog.querySelector('[data-quiz-step]');
      this.results = dialog.querySelector('[data-quiz-results]');
      this.reset();
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog || event.target.closest('[data-quiz-close]')) dialog.close();
      });
      dialog.querySelector('[data-quiz-back]').addEventListener('click', () => {
        if (this.index > 0) { this.index -= 1; this.render(); }
      });
      dialog.querySelector('[data-quiz-restart]').addEventListener('click', () => { this.reset(); this.render(); });
      dialog.querySelector('[data-quiz-add-all]').addEventListener('click', (event) => this.addAll(event.currentTarget));
    }

    reset() {
      this.index = 0;
      this.picks = [];
    }

    open() {
      this.reset();
      this.render();
      this.dialog.showModal();
    }

    render() {
      const total = this.data.questions.length;
      if (this.index >= total) return this.showResults();
      this.step.hidden = false;
      this.results.hidden = true;
      const q = this.data.questions[this.index];
      this.dialog.querySelector('[data-quiz-count]').textContent = (this.data.counter || '')
        .replace('[n]', this.index + 1).replace('[total]', total);
      this.dialog.querySelector('[data-quiz-bar]').style.width = `${((this.index) / total) * 100}%`;
      this.dialog.querySelector('[data-quiz-question]').textContent = q.question;
      const wrap = this.dialog.querySelector('[data-quiz-answers]');
      wrap.innerHTML = '';
      q.answers.forEach((answer, a) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'rq__answer notch-frame';
        if (answer.icon) {
          const icon = document.createElement('span');
          icon.className = 'rq__answer-icon';
          icon.setAttribute('aria-hidden', 'true');
          icon.innerHTML = answer.icon;
          button.appendChild(icon);
        }
        const text = document.createElement('span');
        text.textContent = answer.label;
        button.appendChild(text);
        button.setAttribute('aria-pressed', String(this.picks[this.index] === a));
        button.addEventListener('click', () => {
          this.picks[this.index] = a;
          this.index += 1;
          this.render();
        });
        wrap.appendChild(button);
      });
      this.dialog.querySelector('[data-quiz-back]').hidden = this.index === 0;
    }

    recommended() {
      const tally = new Map();
      let order = 0;
      this.picks.forEach((a, qi) => {
        const answer = this.data.questions[qi] && this.data.questions[qi].answers[a];
        if (!answer) return;
        answer.products.forEach((product) => {
          const entry = tally.get(product.id) || { product: product, score: 0, order: order++ };
          entry.score += 1;
          tally.set(product.id, entry);
        });
      });
      return [...tally.values()]
        .sort((x, y) => (y.score - x.score) || (x.order - y.order))
        .slice(0, this.data.count || 3)
        .map((entry) => entry.product);
    }

    showResults() {
      this.step.hidden = true;
      this.results.hidden = false;
      this.dialog.querySelector('[data-quiz-error]').hidden = true;
      const list = this.dialog.querySelector('[data-quiz-products]');
      list.innerHTML = '';
      this.picked = this.recommended();
      this.picked.forEach((product) => {
        const item = document.createElement('li');
        item.className = 'rq__product';
        const link = document.createElement('a');
        link.href = product.url;
        link.className = 'rq__product-media';
        if (product.image) {
          const img = document.createElement('img');
          img.src = product.image;
          img.alt = '';
          img.loading = 'lazy';
          link.appendChild(img);
        }
        const body = document.createElement('div');
        body.className = 'rq__product-body';
        const title = document.createElement('a');
        title.href = product.url;
        title.className = 'rq__product-title';
        title.textContent = product.title;
        const price = document.createElement('span');
        price.className = 'rq__product-price';
        price.textContent = formatMoney(product.price);
        const holder = document.createElement('span');
        holder.className = 'rq__product-action';
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'rq__add';
        if (!product.available) {
          button.disabled = true;
          button.textContent = this.data.soldOut;
        } else if (product.multi) {
          button.textContent = this.data.addLabel;
          button.setAttribute('data-quick-pick', '');
          const source = document.createElement('script');
          source.type = 'application/json';
          source.setAttribute('data-quick-pick-data', '');
          source.textContent = JSON.stringify({ title: product.title, image: product.image, bg: product.bg, bgDark: product.bgDark, options: product.options, variants: product.variants });
          holder.appendChild(source);
        } else {
          button.textContent = this.data.addLabel;
          button.addEventListener('click', () => this.add([{ id: product.variant, quantity: 1 }], button));
        }
        holder.prepend(button);
        body.append(title, price, holder);
        item.append(link, body);
        list.appendChild(item);
      });
      this.dialog.querySelector('[data-quiz-bar]').style.width = '100%';
    }

    addAll(button) {
      const items = (this.picked || [])
        .filter((product) => product.available)
        .map((product) => {
          const variant = product.variants.find((v) => v.available) || { id: product.variant };
          return { id: variant.id, quantity: 1 };
        });
      if (items.length) this.add(items, button);
    }

    async add(items, button) {
      button.classList.add('is-loading');
      button.disabled = true;
      const url = `${routes.cart_add_url || '/cart/add'}.js`;
      const payload = (list) => ({ items: list, sections: Cart.sectionsToRender().join(','), sections_url: window.location.pathname });
      try {
        let data = null;
        try {
          data = await Cart.request(url, payload(items));
        } catch (bundleError) {
          for (const item of items) {
            try { data = await Cart.request(url, payload([item])); } catch (itemError) { /* skipped */ }
          }
          if (!data) throw bundleError;
        }
        if (data && data.sections) Cart.applySections(data.sections);
        this.dialog.close();
        const drawer = document.getElementById('cart-drawer');
        if (drawer && config.cartType === 'drawer') drawer.open(button);
        else window.location.href = routes.cart_url || '/cart';
      } catch (error) {
        const box = this.dialog.querySelector('[data-quiz-error]');
        box.textContent = (error && (error.description || error.message)) || strings.unavailable || '';
        box.hidden = false;
      } finally {
        button.classList.remove('is-loading');
        button.disabled = false;
      }
    }
  }

  document.querySelectorAll('[data-quiz]').forEach((dialog) => {
    const quiz = new RoutineQuiz(dialog);
    document.querySelectorAll(`[aria-controls="${dialog.id}"][data-quiz-open]`).forEach((button) => {
      button.addEventListener('click', () => quiz.open());
    });
  });

  /* -----------------------------------------------------------------------
     <review-list> — reorders the review cards

     "Featured" restores the order the merchant arranged the blocks in, so the
     original sequence is captured before anything moves.
     -------------------------------------------------------------------- */

  class ReviewList extends HTMLElement {
    connectedCallback() {
      this.cards = [...this.children];
      const root = this.closest('.shopify-section') || document;
      this.select = root.querySelector('[data-review-sort]');
      if (!this.select) return;

      this.select.addEventListener('change', () => this.sort(this.select.value));
    }

    sort(mode) {
      const rating = (el) => parseFloat(el.dataset.rating) || 0;
      let order = this.cards;

      if (mode === 'high') {
        order = [...this.cards].sort((a, b) => rating(b) - rating(a));
      } else if (mode === 'low') {
        order = [...this.cards].sort((a, b) => rating(a) - rating(b));
      }

      order.forEach((card) => this.appendChild(card));
    }
  }

  customElements.define('review-list', ReviewList);

  /* -----------------------------------------------------------------------
     <cart-tabs> — switches the mini cart between cart and favourites
     -------------------------------------------------------------------- */

  class CartTabs extends HTMLElement {
    connectedCallback() {
      this.panes = this.querySelectorAll('[data-pane]');
      this.root = this.closest('.drawer__panel') || document;
      this.tabs = this.root.querySelectorAll('[data-pane-target]');
      this.titles = this.root.querySelectorAll('[data-pane-title]');
      if (!this.tabs.length) return;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.show(tab.dataset.paneTarget));
      });

      // Cart.applySections swaps the drawer markup after every add / change,
      // which would drop the shopper back on the cart. While the drawer is
      // open, put them back on the pane they were looking at.
      const drawer = this.closest('drawer-element');
      const last = this.root !== document ? this.root.dataset.activePane : '';
      if (last && last !== 'cart' && drawer && drawer.classList.contains('is-open')) {
        this.show(last);
      }
    }

    show(name) {
      if (this.root !== document) this.root.dataset.activePane = name;
      this.panes.forEach((pane) => {
        pane.hidden = pane.dataset.pane !== name;
      });
      this.tabs.forEach((tab) => {
        const active = tab.dataset.paneTarget === name;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
      });
      this.titles.forEach((title) => {
        title.hidden = title.dataset.paneTitle !== name;
      });
      this.dispatchEvent(new CustomEvent('pane:change', { bubbles: true, detail: { pane: name } }));
    }
  }

  customElements.define('cart-tabs', CartTabs);

  /* -----------------------------------------------------------------------
     <wishlist-panel> — renders saved items

     Saved products live in localStorage, so they have to be looked up at
     render time. Each handle is fetched once from Shopify's product JSON and
     cached for the page.
     -------------------------------------------------------------------- */

  const productCache = {};

  class WishlistPanel extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-wishlist-items]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      if (!this.list) return;

      this.render();
      document.addEventListener('wishlist:change', () => this.render());
      document.addEventListener('cart:updated', () => this.render());
    }

    async render() {
      // A drawer re-render leaves the old panel's listeners behind.
      if (!this.isConnected) return;
      const saved = wishlist.read().filter((entry) => entry.handle);

      if (!saved.length) {
        this.list.innerHTML = '';
        if (this.empty) this.empty.hidden = false;
        return;
      }

      if (this.empty) this.empty.hidden = true;

      const products = await Promise.all(
        saved.map(async (entry) => {
          if (productCache[entry.handle]) return productCache[entry.handle];
          try {
            const response = await fetch(
              `${routes.root === '/' ? '' : routes.root}/products/${entry.handle}.js`
            );
            if (!response.ok) return null;
            const data = await response.json();
            productCache[entry.handle] = data;
            return data;
          } catch (error) {
            return null;
          }
        })
      );

      const render = this.dataset.style === 'ticket' ? (p) => this.ticketCard(p) : (p) => this.card(p);
      this.list.innerHTML = products.filter(Boolean).map(render).join('');
    }

    /* Mini ticket card for the version 2 drawer: the same markup and classes
       as the cart recommendations (sections/cart-drawer-recs.liquid), plus a
       yellow heart that un-saves the product through <wishlist-button>. */
    ticketCard(product) {
      const esc = (value) =>
        String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({
          '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
        })[c]);
      const variant = (product.variants || [])[0] || {};
      const single = (product.variants || []).length === 1 && variant.available;
      const title = esc(product.title);
      const url = esc(product.url);
      const image = product.featured_image
        ? `<img src="${esc(product.featured_image.replace(/(\.[a-z]+)(\?|$)/i, '_360x$1$2'))}" alt="" width="180" height="180" loading="lazy">`
        : '';
      const iconTpl = this.querySelector('template[data-wishlist-remove-icon]');
      const icon = iconTpl ? iconTpl.innerHTML : '&times;';
      const removeLabel = esc(this.dataset.removeLabel || 'Remove');

      const action = single
        ? `<product-form class="mc2-rec__form" data-open-drawer="false">
             <form method="post" action="${routes.cart_add_url || '/cart/add'}" accept-charset="UTF-8" enctype="multipart/form-data">
               <input type="hidden" name="form_type" value="product">
               <input type="hidden" name="utf8" value="✓">
               <input type="hidden" name="id" value="${variant.id}">
               <input type="hidden" name="quantity" value="1">
               <button type="submit" class="mc2-rec__add" data-add-to-cart>
                 <span data-button-label>${esc(this.dataset.addLabel || 'Add to cart')}</span>
               </button>
               <p class="mc2-rec__error" data-cart-error role="alert" hidden></p>
             </form>
           </product-form>`
        : `<a href="${url}" class="mc2-rec__add">${esc(this.dataset.optionsLabel || 'Choose options')}</a>`;

      return `<div class="mc2-rec mc2-fav">
        <div class="mc2-rec__frame notch-frame">
          <div class="mc2-rec__inner notch-frame">
            <a href="${url}" class="mc2-rec__media" tabindex="-1" aria-hidden="true">${image}</a>
            <wishlist-button class="mc2-fav__remove">
              <button type="button" data-wishlist="${esc(product.id)}" data-wishlist-handle="${esc(product.handle)}" aria-label="${removeLabel} - ${title}">${icon}</button>
            </wishlist-button>
            <div class="mc2-rec__body">
              <a href="${url}" class="mc2-rec__title">${title}</a>
              <span class="mc2-rec__price">${formatMoney(variant.price || product.price)}</span>
              ${action}
            </div>
          </div>
        </div>
      </div>`;
    }

    card(product) {
      const variant = (product.variants || [])[0] || {};
      const single = (product.variants || []).length === 1 && variant.available;
      const image = product.featured_image
        ? `<img src="${product.featured_image.replace(/(\.[a-z]+)(\?|$)/i, '_300x$1$2')}" alt="" width="150" height="150" loading="lazy">`
        : '';

      const action = single
        ? `<product-form data-open-drawer="false">
             <form method="post" action="${routes.cart_add_url || '/cart/add'}" accept-charset="UTF-8" enctype="multipart/form-data">
               <input type="hidden" name="form_type" value="product">
               <input type="hidden" name="utf8" value="✓">
               <input type="hidden" name="id" value="${variant.id}">
               <button type="submit" class="fav-card__add" data-add-to-cart>
                 <span data-button-label>${this.dataset.addLabel || 'Add to cart'}</span>
               </button>
             </form>
           </product-form>`
        : `<a href="${product.url}" class="fav-card__add">${this.dataset.optionsLabel || 'Choose options'}</a>`;

      return `<div class="fav-card">
        <a href="${product.url}" class="fav-card__media media media--square media--contain" tabindex="-1" aria-hidden="true">${image}</a>
        <div class="fav-card__body">
          <a href="${product.url}" class="fav-card__title">${product.title}</a>
          <span class="fav-card__price">${formatMoney(variant.price || product.price)}</span>
          ${action}
        </div>
      </div>`;
    }
  }

  customElements.define('wishlist-panel', WishlistPanel);

  /* -----------------------------------------------------------------------
     <video-card> — swaps a cover image for the real video on click

     Keeps the markup light until someone actually wants to watch.
     -------------------------------------------------------------------- */

  class VideoCard extends HTMLElement {
    connectedCallback() {
      const trigger = this.querySelector('.ugc-card__trigger');
      const template = this.querySelector('template[data-video]');
      if (!trigger || !template) return;

      trigger.addEventListener(
        'click',
        () => {
          const player = template.content.cloneNode(true);
          trigger.replaceWith(player);
          this.classList.add('is-playing');
        },
        { once: true }
      );
    }
  }

  customElements.define('video-card', VideoCard);

  /* -----------------------------------------------------------------------
     <theme-toggle> — dark / light switch

     The initial mode is resolved inline in the <head> before first paint;
     this only handles flipping and remembering the choice.
     -------------------------------------------------------------------- */

  class ThemeToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;

      this.sync();
      this.button.addEventListener('click', () => {
        const next =
          document.documentElement.getAttribute('data-theme') === 'light' ? 'dark' : 'light';
        document.documentElement.setAttribute('data-theme', next);
        try {
          localStorage.setItem('mb-theme', next);
        } catch (error) {
          /* Storage blocked — the choice just won't persist. */
        }
        this.sync();
        document.dispatchEvent(
          new CustomEvent('theme:mode', { bubbles: true, detail: { mode: next } })
        );
      });
    }

    sync() {
      const mode = document.documentElement.getAttribute('data-theme') || 'dark';
      this.button.setAttribute('aria-pressed', String(mode === 'light'));
      this.querySelectorAll('[data-mode-label]').forEach((label) => {
        label.hidden = label.dataset.modeLabel !== mode;
      });
    }
  }

  customElements.define('theme-toggle', ThemeToggle);

  /* -----------------------------------------------------------------------
     Boot
     -------------------------------------------------------------------- */

  /* Ticket product page: on desktop the main photo frame takes the info
     panel's height, so the two boxes line up top and bottom. */
  function initTicketGallery() {
    if (!('ResizeObserver' in window)) return;
    document.querySelectorAll('.product-section--ticket .product').forEach((product) => {
      const info = product.querySelector('.product__info');
      if (!info || product.dataset.infoMatch) return;
      product.dataset.infoMatch = 'true';
      new ResizeObserver(() => {
        product.style.setProperty('--pdp-info-h', `${Math.round(info.getBoundingClientRect().height)}px`);
      }).observe(info);
    });
  }

  function init() {
    initHeader();
    initTicketGallery();
    initReveal();
    initSystemRails();
    syncWishlistCounts();
  }

  // Re-rendered drawer markup arrives with a zeroed favourites count.
  document.addEventListener('cart:updated', syncWishlistCounts);

  // Once per page, not on every section reload in the editor.
  initPageTransition();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-run setup when a section is added or reloaded.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', init);

  // Judge.me review tickets clamp the text; a tap opens the full review.
  document.addEventListener('click', (event) => {
    const body = event.target.closest('.mb-judgeme .jdgm-review-content__body-content');
    if (body && !event.target.closest('a, button')) body.classList.toggle('is-open');
  });

  window.MrBeardTheme = { init, initReveal, trapFocus, debounce, formatMoney, fetchSection };
})();
